import { useState, useEffect, useRef, useCallback } from 'react';

const useIdleTimer = ({
  timeout = 600000, // 10 minutes default
  warningTime = 480000, // 8 minutes default
  onIdle,
  onWarning,
  onActive,
  enabled = true
}) => {
  const [isIdle, setIsIdle] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(timeout);
  const [lastActivity, setLastActivity] = useState(Date.now());
  
  const timeoutRef = useRef();
  const warningTimeoutRef = useRef();
  const intervalRef = useRef();
  const activitiesRef = useRef([]);

  // Events to track for user activity. Jun 2026 — 'focus' removed:
  // programmatic focus (modals, autofocus inputs, polling re-renders)
  // reset the idle clock without real user activity, desynchronizing
  // the frontend timer from the backend activity clock.
  const events = [
    'mousedown',
    'mousemove', 
    'keydown',
    'wheel',
    'scroll',
    'touchstart',
    'click'
  ];

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);
  }, []);

  const handleActivity = useCallback(() => {
    if (!enabled) return;

    const now = Date.now();
    setLastActivity(now);
    setRemainingTime(timeout);
    
    // Reset idle state if user was idle
    if (isIdle) {
      setIsIdle(false);
      onActive && onActive();
    }

    // Hide warning if showing
    if (showWarning) {
      setShowWarning(false);
    }

    // Clear existing timers
    clearTimers();

    // Start warning timer
    warningTimeoutRef.current = setTimeout(() => {
      if (enabled) {
        setShowWarning(true);
        onWarning && onWarning();
      }
    }, warningTime);

    // Start idle timer
    timeoutRef.current = setTimeout(() => {
      if (enabled) {
        setIsIdle(true);
        setShowWarning(false);
        onIdle && onIdle();
      }
    }, timeout);
  }, [enabled, timeout, warningTime, isIdle, showWarning, onIdle, onWarning, onActive, clearTimers]);

  const reset = useCallback(() => {
    handleActivity();
  }, [handleActivity]);

  const pause = useCallback(() => {
    clearTimers();
  }, [clearTimers]);

  const resume = useCallback(() => {
    if (enabled) {
      handleActivity();
    }
  }, [enabled, handleActivity]);

  // Update remaining time countdown
  useEffect(() => {
    if (showWarning && enabled) {
      intervalRef.current = setInterval(() => {
        setRemainingTime(prev => {
          const newTime = prev - 1000;
          return newTime > 0 ? newTime : 0;
        });
      }, 1000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [showWarning, enabled]);

  // Setup activity listeners
  useEffect(() => {
    if (!enabled) {
      clearTimers();
      return;
    }

    const handleActivityEvent = (e) => {
      // Ignore background API calls - only track user interactions
      if (e.type === 'fetch' || e.type === 'xhr') return;
      // IDLE-LOGOUT-BTN-001 (Jun 2026) — ignore events originating inside
      // the idle warning modal. This listener is capture-phase, so without
      // this guard it resets the idle timer (and via onActive unmounts the
      // modal) on the mousedown of a button click — the click then never
      // fires on the removed button.
      if (
        e && e.target && typeof e.target.closest === 'function' &&
        e.target.closest('[data-idle-modal]')
      ) {
        return;
      }
      handleActivity();
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivityEvent, true);
    });

    // Initialize timer
    handleActivity();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivityEvent, true);
      });
      clearTimers();
    };
  }, [enabled, handleActivity, clearTimers]);

  // API call tracking - DISABLED for security
  // Background API calls (polling, refresh, etc.) should NOT reset the idle timer
  // Only actual user interactions (mouse, keyboard, touch) count as activity
  const trackApiActivity = useCallback(() => {
    // SECURITY: Do NOT reset idle timer on API calls
    // This is intentionally a no-op to ensure proper idle timeout
    // Only actual user interactions tracked via DOM events should reset the timer
    return;
  }, []);

  return {
    isIdle,
    showWarning,
    remainingTime: Math.ceil(remainingTime / 1000), // Convert to seconds
    warningTime: Math.ceil((timeout - warningTime) / 1000), // Grace period in seconds
    lastActivity,
    reset,
    pause,
    resume,
    trackApiActivity
  };
};

export default useIdleTimer;